import React, { useMemo } from 'react';
import { Badge, Card, Drawer, Space, Typography } from 'antd';
import { Line } from '@ant-design/charts';
import { Link } from 'react-router-dom';
import type { PvpLiveCheck, PvpLiveCheckKey, PvpLiveRange } from '@/types/pvp';
import {
  CHECK_MEANING,
  STATUS_BADGE,
  STATUS_WORD,
  checkRows,
  fmtCheckValue,
  fmtNum,
  limitsLine,
  timeAxis,
  tooltipTitle,
} from './model';

const { Paragraph, Text } = Typography;

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 8,
  width: '100%',
  padding: '6px 4px',
  background: 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
};

/** One check: its dot, label and value. Pressing it opens the check's drawer. */
export const HealthRow: React.FC<{
  check: PvpLiveCheck;
  onOpen: (key: PvpLiveCheckKey) => void;
}> = ({ check, onOpen }) => (
  <button
    type="button"
    data-check={check.key}
    data-status={check.status}
    aria-label={`${check.label}: ${fmtCheckValue(check)}, ${STATUS_WORD[check.status]}. Open its chart.`}
    onClick={() => onOpen(check.key)}
    style={ROW_STYLE}
  >
    <Badge status={STATUS_BADGE[check.status]} />
    <span style={{ flex: '1 1 120px' }}>{check.label}</span>
    <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {fmtCheckValue(check)}
    </span>
  </button>
);

/** The 11 checks of the last 15 minutes, in contract order. */
export const HealthList: React.FC<{
  checks: PvpLiveCheck[];
  onOpen: (key: PvpLiveCheckKey) => void;
}> = ({ checks, onOpen }) => (
  <Card size="small" title="Health, last 15 min" style={{ height: '100%' }}>
    {checks.map((check) => (
      <HealthRow key={check.key} check={check} onOpen={onOpen} />
    ))}
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
      Green good, orange warning, red bad, grey idle (nothing to measure). Press a check for its
      chart and limits.
    </Text>
  </Card>
);

/** What the drawer shows for one check: value, limits, meaning and its chart over the range. */
export const CheckDetail: React.FC<{
  check: PvpLiveCheck;
  range: PvpLiveRange;
  bucketMin: number;
}> = ({ check, range, bucketMin }) => {
  const rows = useMemo(() => checkRows(check), [check]);
  return (
    <div data-check-detail={check.key}>
      <Space size={12} wrap>
        <Badge status={STATUS_BADGE[check.status]} text={STATUS_WORD[check.status]} />
        <Text strong style={{ fontSize: 18 }}>
          {fmtCheckValue(check)}
        </Text>
      </Space>
      <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
        Last 15 minutes. Limits: {limitsLine(check)}.
      </Text>
      <Paragraph style={{ marginTop: 12 }}>{CHECK_MEANING[check.key]}</Paragraph>
      {check.key === 'sync' && (
        <Paragraph>
          <Link to="/pvp-sync">Open PvP Sync</Link> for the per-match detail.
        </Paragraph>
      )}
      <Line
        data={rows}
        xField="t"
        yField="value"
        height={220}
        axis={timeAxis(range)}
        tooltip={{
          title: tooltipTitle(range),
          items: [
            {
              channel: 'y',
              name: check.label,
              valueFormatter: (v: number) => fmtCheckValue({ value: v, unit: check.unit }),
            },
          ],
        }}
        animate={false}
      />
      <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
        Each point is the same formula over one {fmtNum(bucketMin, 0)}-minute bucket (IST times). A
        gap is a bucket with nothing to measure.
      </Text>
    </div>
  );
};

/** The drawer of the open check; closed when `check` is null. */
export const CheckDrawer: React.FC<{
  check: PvpLiveCheck | null;
  range: PvpLiveRange;
  bucketMin: number;
  onClose: () => void;
}> = ({ check, range, bucketMin, onClose }) => (
  <Drawer
    open={check != null}
    onClose={onClose}
    title={check?.label}
    width="min(560px, 100vw)"
    destroyOnHidden
  >
    {check && <CheckDetail check={check} range={range} bucketMin={bucketMin} />}
  </Drawer>
);
